/**
 * Lift2Fit — one completed session, opened as a table in the Grid's language.
 *
 * The same component serves both sides of the relationship. The lifter opens
 * their own session from the Grid's history; the coach opens a client's from
 * the client page. Everything except the last layer is identical:
 *
 *   * the header facts (date, label, duration, volume, sets done vs prescribed,
 *     average RPE against target, and the week/day of the block it came from);
 *   * the prescription beside every logged set, so the difference is read, not
 *     calculated;
 *   * the server's compliance summary — counts and words, never a score.
 *
 * The anomaly layer is **opt-in** (`showFlags`): the API only attaches `flags`
 * to a coach-scoped read, and the client only renders the flag panel and the
 * row highlighting when the caller asks for it. The lifter's screen is the same
 * table minus that layer — no flags, no highlighting, no empty-flag banner.
 *
 * Colour is never the only signal: a flagged row carries the flag's word and
 * the alert glyph in its own cell as well as the row tint.
 */
import { useMemo } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import {
  api,
  type ExerciseGroupType,
  type SessionExercise,
  type SessionFlagCode,
  type SessionFlagSeverity,
  type SessionFlagWire,
  type SessionSet,
  type SessionViewWire,
  type SetMeasure,
} from '@/lib/api';
import { cn, formatDate } from '@/lib/format';
import { formatVolume, kgToDisplay } from '@/lib/units';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { AlertIcon, CheckIcon, GridIcon, SparkIcon } from '@/lib/icons';
import { StatusTag } from '@/components/StatusTag';
import {
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
  groupTagText,
  type ChipTone,
} from './components';
import { useAsync } from './hooks';
import { Link } from './router';

/* ---------------------------------------------------------------------------
   The prescription snapshot, read from the session's own jsonb
   ------------------------------------------------------------------------ */

interface PrescriptionView {
  sets: number;
  repMin: number | null;
  repMax: number | null;
  targetRpe: number | null;
  targetWeightKg: number | null;
  restSeconds: number;
  tempo: string | null;
  measure: SetMeasure;
  perSide: boolean;
  supersetGroup: number | null;
  groupType: ExerciseGroupType | null;
}

const MEASURES: readonly SetMeasure[] = ['reps', 'distance_m', 'time_s', 'amrap'];

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parsePrescription(raw: Record<string, unknown>): PrescriptionView {
  const measure = MEASURES.includes(raw.measure as SetMeasure) ? (raw.measure as SetMeasure) : 'reps';
  const supersetGroup = asNumber(raw.supersetGroup);
  const groupTypeRaw = raw.groupType;
  return {
    sets: Math.max(0, Math.round(asNumber(raw.sets) ?? 0)),
    repMin: asNumber(raw.repMin),
    repMax: asNumber(raw.repMax),
    targetRpe: asNumber(raw.targetRpe),
    targetWeightKg: asNumber(raw.targetWeightKg),
    restSeconds: Math.max(0, Math.round(asNumber(raw.restSeconds) ?? 0)),
    tempo: typeof raw.tempo === 'string' ? raw.tempo : null,
    measure,
    perSide: raw.perSide === true,
    supersetGroup,
    groupType:
      groupTypeRaw === 'drop_set' ? 'drop_set' : groupTypeRaw === 'superset' || supersetGroup !== null ? 'superset' : null,
  };
}

function repRange(prescription: PrescriptionView): string {
  if (prescription.repMin === null && prescription.repMax === null) return '—';
  if (prescription.repMin === prescription.repMax) return String(prescription.repMin ?? prescription.repMax ?? '—');
  return `${prescription.repMin ?? '—'}–${prescription.repMax ?? '—'}`;
}

/** "3 × 8–12 · 100 kg · RPE 7 · rest 120s · tempo 3-1-1-0" */
function prescriptionLine(prescription: PrescriptionView, unit: UnitSystem): string {
  const parts: string[] = [`${prescription.sets} × ${repRange(prescription)}`];
  if (prescription.measure === 'distance_m') parts.push('metres');
  else if (prescription.measure === 'time_s') parts.push('seconds');
  else if (prescription.measure === 'amrap') parts.push('AMRAP');
  else if (prescription.targetWeightKg !== null && prescription.targetWeightKg > 0) {
    parts.push(`${kgToDisplay(prescription.targetWeightKg, unit)} ${unit === 'imperial' ? 'lb' : 'kg'}`);
  } else parts.push('BW / auto load');
  if (prescription.targetRpe !== null) parts.push(`RPE ${prescription.targetRpe}`);
  parts.push(prescription.restSeconds ? `rest ${prescription.restSeconds}s` : 'rest as needed');
  if (prescription.tempo) parts.push(`tempo ${prescription.tempo}`);
  return parts.join(' · ');
}

/** The per-set target, printed beside the logged set that was meant to match it. */
function setTargetLabel(prescription: PrescriptionView, set: SessionSet, unit: UnitSystem): string {
  if (prescription.measure === 'distance_m') return `${prescription.repMax ?? prescription.repMin ?? 0} m`;
  if (prescription.measure === 'time_s') return `${prescription.repMax ?? prescription.repMin ?? 0} s`;
  const load =
    prescription.targetWeightKg === null || prescription.targetWeightKg <= 0
      ? 'BW'
      : `${kgToDisplay(prescription.targetWeightKg, unit)} ${unit === 'imperial' ? 'lb' : 'kg'}`;
  const reps = prescription.repMin === prescription.repMax ? `${prescription.repMax ?? ''}` : repRange(prescription);
  const warm = set.isWarmup ? ' (warm-up)' : '';
  return `${load} × ${reps}${warm}`;
}

/** "100 kg × 12" / "500 m" / "45s" — what was actually logged. */
function setActualLabel(set: SessionSet, unit: UnitSystem): string {
  if (set.measure === 'distance_m') return `${set.distanceM ?? 0} m`;
  if (set.measure === 'time_s') return `${set.durationS ?? 0}s`;
  if (set.measure === 'amrap') return `${set.reps} reps (AMRAP)`;
  const load = set.weightKg > 0 ? `${kgToDisplay(set.weightKg, unit)} ${unit === 'imperial' ? 'lb' : 'kg'}` : 'BW';
  return `${load} × ${set.reps}${set.perSide ? '/side' : ''}`;
}

/** Rest before this set, derived from the logged `performedAt` instants. */
function restBefore(sets: readonly SessionSet[], index: number): number | null {
  if (index <= 0) return null;
  const previous = sets[index - 1];
  const current = sets[index];
  if (!previous || !current) return null;
  const from = Date.parse(previous.performedAt);
  const to = Date.parse(current.performedAt);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;
  return Math.round((to - from) / 1000);
}

/* ---------------------------------------------------------------------------
   The coach's flag vocabulary — word + colour + icon, never colour alone
   ------------------------------------------------------------------------ */

const FLAG_LABELS: Record<SessionFlagCode, string> = {
  sets_skipped: 'Sets skipped',
  sets_added: 'Sets added',
  rep_range_missed: 'Rep range missed',
  load_below_prescription: 'Load below prescription',
  load_above_prescription: 'Load above prescription',
  rpe_far_from_target: 'RPE far from target',
  rest_too_short: 'Rest too short',
  rest_too_long: 'Rest too long',
  tempo_not_followed: 'Tempo not followed',
  pain_reported: 'Pain reported',
  warmup_logged_as_working: 'Warm-up logged as working',
  movement_missing: 'Movement missing',
  movement_substituted: 'Movement substituted',
};

const FLAG_TONES: Record<SessionFlagSeverity, ChipTone> = {
  danger: 'danger',
  warning: 'warning',
  info: 'info',
};

const FLAG_WORDS: Record<SessionFlagSeverity, string> = {
  danger: 'Action',
  warning: 'Watch',
  info: 'Note',
};

const FLAG_ROW_TONES: Record<SessionFlagSeverity, string> = {
  danger: 'bg-danger/[0.06]',
  warning: 'bg-warning/[0.07]',
  info: 'bg-info/[0.05]',
};

function flagLabel(code: SessionFlagCode): string {
  return FLAG_LABELS[code];
}

function highestSeverity(flags: readonly SessionFlagWire[]): SessionFlagSeverity {
  if (flags.some((flag) => flag.severity === 'danger')) return 'danger';
  if (flags.some((flag) => flag.severity === 'warning')) return 'warning';
  return 'info';
}

/* ---------------------------------------------------------------------------
   The shared view
   ------------------------------------------------------------------------ */

export interface SessionDetailViewProps {
  sessionId: string;
  /** The client's id for a coach read; null/omitted for the lifter's own session. */
  clientId?: string | null;
  /**
   * The coach-only anomaly layer. Opt-in: omitted or false renders exactly the
   * lifter's table — no flag panel and no row highlighting.
   */
  showFlags?: boolean;
  backTo: string;
  backLabel: string;
  /** Whose session this is, e.g. the client's name in the coach's lead sentence. */
  ownerLabel?: string | null;
}

const SESSION_STATUS: Record<SessionViewWire['session']['status'], { label: string; tone: ChipTone }> = {
  completed: { label: 'Completed', tone: 'success' },
  abandoned: { label: 'Abandoned', tone: 'danger' },
  active: { label: 'In progress', tone: 'accent' },
};

export function SessionDetailView({
  sessionId,
  clientId = null,
  showFlags = false,
  backTo,
  backLabel,
  ownerLabel = null,
}: SessionDetailViewProps) {
  const { unit } = useUnitPreference();
  const state = useAsync(() => api.sessionView(sessionId, clientId), [sessionId, clientId]);
  const view = state.data;

  const flags = useMemo(() => (showFlags ? view?.flags ?? [] : []), [showFlags, view]);
  const flagsByExercise = useMemo(() => {
    const map = new Map<string, SessionFlagWire[]>();
    for (const flag of flags) {
      const list = map.get(flag.target.sessionExerciseId);
      if (list) list.push(flag);
      else map.set(flag.target.sessionExerciseId, [flag]);
    }
    return map;
  }, [flags]);

  const backLink = (
    <Link
      to={backTo}
      data-testid="session-detail-back"
      className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
    >
      {backLabel}
    </Link>
  );

  if (state.loading && !view) {
    return <LoadingPanel label="Opening the session" rows={6} />;
  }

  if (!view) {
    return (
      <EmptyState
        title="We could not open that session"
        body={state.error ?? 'The session may have been removed, or the link may not allow it.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={backLink}
      />
    );
  }

  const status = SESSION_STATUS[view.session.status];
  const block = view.block;
  const blockSentence = block
    ? `${block.planName} — week ${block.weekOf} of ${block.weeks} · ${block.dayLabel}${block.isDeload ? ' · deload week' : ''}`
    : 'An ad-hoc session, not run from a programme day.';
  const lead = [formatDate(view.session.startedAt), ownerLabel, blockSentence].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-6" data-testid="session-detail">
      <ScreenHeader
        eyebrow={showFlags ? 'Coaching workspace' : 'The Grid'}
        title={view.session.label ?? 'Session'}
        badge={<StatusChip tone={status.tone}>{status.label}</StatusChip>}
        lead={lead}
        actions={backLink}
      />

      {/* ---- header facts ------------------------------------------------ */}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="session-header-facts">
        <MetricTile label="Duration" value={`${view.totals.durationMin} min`} hint={formatDate(view.session.startedAt)} />
        <MetricTile label="Volume" value={formatVolume(view.totals.volumeKg, unit)} hint="logged working sets" />
        <MetricTile
          label="Sets"
          value={`${view.compliance.setsCompleted}/${view.compliance.setsPrescribed}`}
          hint={`${view.compliance.exercisesCompleted} of ${view.compliance.exercisesPrescribed} movements trained`}
          hintTone={view.compliance.setsCompleted >= view.compliance.setsPrescribed ? 'success' : 'warning'}
        />
        <MetricTile
          label="Avg RPE"
          value={view.compliance.avgRpe === null ? '—' : String(view.compliance.avgRpe)}
          hint={view.compliance.targetRpe === null ? 'no RPE target' : `target ${view.compliance.targetRpe}`}
          hintTone={
            view.compliance.avgRpe === null || view.compliance.targetRpe === null
              ? 'default'
              : Math.abs(view.compliance.avgRpe - view.compliance.targetRpe) <= 0.5
                ? 'success'
                : 'warning'
          }
        />
        <MetricTile label="PRs" value={String(view.totals.prCount)} hint="new personal records" hintTone={view.totals.prCount > 0 ? 'accent' : 'default'} />
        <MetricTile
          label="Failed sets"
          value={String(view.totals.failedSets)}
          hint={view.totals.failedSets > 0 ? 'logged as failed' : 'none logged'}
          hintTone={view.totals.failedSets > 0 ? 'danger' : 'success'}
        />
      </dl>

      {/* ---- session compliance ------------------------------------------ */}
      <Panel
        testId="session-compliance"
        title="Session compliance"
        subtitle="The log read against the prescription that was snapshotted when the session started."
      >
        <p className="text-[14px] font-bold text-ink" data-testid="session-compliance-summary">
          {view.compliance.summary}
        </p>
        <ul className="flex flex-col divide-y divide-hairline" data-testid="session-compliance-exercises">
          {view.sessionExercises.map((exercise, index) => {
            const entry = view.compliance.exercises.find(
              (candidate) => candidate.sessionExerciseId === exercise.id,
            );
            if (!entry) return null;
            return (
              <li
                key={exercise.id}
                data-testid={`session-compliance-exercise-${index}`}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <span className="text-[13px] font-bold text-ink">{exercise.exerciseName}</span>
                <span className="flex flex-wrap items-center gap-1.5">
                  <StatusChip
                    tone={entry.setsCompleted >= entry.setsPrescribed ? 'success' : 'warning'}
                  >
                    {entry.setsCompleted}/{entry.setsPrescribed} sets
                  </StatusChip>
                  <StatusChip tone={entry.repRangeMet ? 'success' : 'warning'}>
                    {entry.repRangeMet ? 'Rep range met' : 'Rep range missed'}
                  </StatusChip>
                  {entry.loadOnTarget === null ? null : (
                    <StatusChip tone={entry.loadOnTarget ? 'success' : 'warning'}>
                      {entry.loadOnTarget ? 'Load on target' : 'Load off target'}
                    </StatusChip>
                  )}
                  {entry.avgRpe !== null ? <StatusChip tone="neutral">Avg RPE {entry.avgRpe}</StatusChip> : null}
                </span>
              </li>
            );
          })}
        </ul>
      </Panel>

      {/* ---- the coach layer, opt-in only -------------------------------- */}
      {showFlags ? (
        <Panel
          testId="session-coach-flags"
          title="Anomalies"
          subtitle="Found deterministically by the coach engine's thresholds. Each flag carries its evidence and one suggested next step."
        >
          <div data-testid="coach-flag-layer">
            {flags.length ? (
              <ul className="flex flex-col gap-3">
                {flags.map((flag, index) => (
                  <li
                    key={`${flag.code}-${flag.target.sessionExerciseId}-${flag.target.setIndex ?? 'movement'}-${index}`}
                    data-testid={`session-flag-${flag.code}${flag.target.setIndex === null ? '' : `-set-${flag.target.setIndex}`}`}
                    className={cn('flex flex-col gap-1.5 border border-hairline p-3', FLAG_ROW_TONES[flag.severity])}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <StatusChip tone={FLAG_TONES[flag.severity]} icon={<AlertIcon />}>
                        {FLAG_WORDS[flag.severity]}
                      </StatusChip>
                      <span className="text-[14px] font-bold text-ink">{flagLabel(flag.code)}</span>
                      <StatusChip tone="neutral">
                        {flag.source === 'evaluation_matrix' ? 'Evaluation matrix' : 'Session log'}
                      </StatusChip>
                    </span>
                    <p className="text-[13px] text-ink">{flag.evidence}</p>
                    <p className="text-[13px] text-ink-muted">
                      <span className="font-bold uppercase tracking-[0.06em] text-accent">Next: </span>
                      {flag.suggestedAction}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-ink-muted" data-testid="session-flags-empty">
                <span className="mr-1 inline-flex items-center gap-1 font-bold text-success">
                  <CheckIcon /> Nothing flagged
                </span>
                — every logged set matched the prescription. The table below is the evidence.
              </p>
            )}
          </div>
        </Panel>
      ) : null}

      {/* ---- the table --------------------------------------------------- */}
      <Panel
        testId="session-table-panel"
        title="What was actually logged"
        subtitle="Each set with the prescription beside it. Rest is derived from the times the sets were logged."
      >
        <div className="flex flex-col gap-5">
          {view.sessionExercises.map((exercise, exerciseIndex) => (
            <ExerciseTable
              key={exercise.id}
              exercise={exercise}
              index={exerciseIndex}
              unit={unit}
              showFlags={showFlags}
              flags={flagsByExercise.get(exercise.id) ?? []}
            />
          ))}
          {view.sessionExercises.length === 0 ? (
            <p className="flex items-center gap-2 text-[13px] text-ink-muted">
              <SparkIcon className="h-5 w-5 text-accent" />
              No movements were recorded in this session.
            </p>
          ) : null}
        </div>
      </Panel>

      <p className="flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
        <GridIcon className="h-4 w-4" />
        {showFlags
          ? 'You are reading this as the coach: flags and highlighting are yours alone. The client sees the same table without them.'
          : 'This is your log exactly as you recorded it — the prescription beside every set, nothing added.'}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   One movement's table
   ------------------------------------------------------------------------ */

function ExerciseTable({
  exercise,
  index,
  unit,
  showFlags,
  flags,
}: {
  exercise: SessionExercise;
  index: number;
  unit: UnitSystem;
  showFlags: boolean;
  flags: readonly SessionFlagWire[];
}) {
  const prescription = parsePrescription(exercise.prescription);
  const sets = [...exercise.loggedSets].sort((a, b) => a.setIndex - b.setIndex);
  const movementFlags = flags.filter((flag) => flag.target.setIndex === null);

  return (
    <section className="flex flex-col gap-2" data-testid={`session-exercise-${index}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-ink">
          {exercise.exerciseName}
          {prescription.supersetGroup !== null ? (
            <StatusChip tone="info">{groupTagText(prescription.supersetGroup, prescription.groupType)}</StatusChip>
          ) : null}
        </span>
        <span
          className="l2f-tabular font-mono text-[12px] text-ink-muted"
          data-testid={`session-prescription-${index}`}
        >
          Prescribed {prescriptionLine(prescription, unit)}
        </span>
      </div>
      {exercise.notes ? <p className="text-[12px] text-ink-muted">Cue: {exercise.notes}</p> : null}

      {movementFlags.length ? (
        <ul className="flex flex-col gap-1" data-testid={`session-movement-flags-${index}`}>
          {movementFlags.map((flag) => (
            <li key={flag.code} className="flex flex-wrap items-center gap-2 text-[12px] text-ink">
              <StatusChip tone={FLAG_TONES[flag.severity]} icon={<AlertIcon />}>
                {FLAG_WORDS[flag.severity]}
              </StatusChip>
              <span className="font-bold">{flagLabel(flag.code)}</span>
              <span className="text-ink-muted">{flag.evidence}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse text-left" data-testid={`session-sets-${index}`}>
          <thead>
            <tr className="border-b border-hairline">
              {['Set', 'Logged', 'Prescribed', 'RPE', 'Rest', 'Status', ...(showFlags ? ['Anomaly'] : [])].map(
                (heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                  >
                    {heading}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {sets.map((set, setIndex) => {
              const setFlags = flags.filter((flag) => flag.target.setIndex === set.setIndex);
              const severity = setFlags.length ? highestSeverity(setFlags) : null;
              const rest = restBefore(sets, setIndex);
              const pain = set.painScore;
              return (
                <tr
                  key={set.id}
                  data-testid={`session-set-${index}-${set.setIndex}`}
                  data-flagged={severity ?? undefined}
                  className={cn(
                    'border-b border-hairline last:border-b-0',
                    severity ? FLAG_ROW_TONES[severity] : set.isWarmup ? 'bg-page-subtle/60' : undefined,
                  )}
                >
                  <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">{set.setIndex}</td>
                  <td className="l2f-tabular px-2 py-2 font-mono text-[13px] font-bold text-ink">
                    {setActualLabel(set, unit)}
                  </td>
                  <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink-muted">
                    {setTargetLabel(prescription, set, unit)}
                  </td>
                  <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">
                    {set.rpe === null ? '—' : String(set.rpe)}
                    {prescription.targetRpe !== null ? (
                      <span className="text-ink-muted"> / {prescription.targetRpe}</span>
                    ) : null}
                  </td>
                  <td className="l2f-tabular px-2 py-2 font-mono text-[13px] text-ink">
                    {rest === null ? '—' : `${rest}s`}
                    {prescription.restSeconds ? (
                      <span className="text-ink-muted"> / {prescription.restSeconds}s</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <StatusTag status={set.isPr ? 'pr' : set.status} />
                      {set.isWarmup ? <StatusChip tone="neutral">Warm-up</StatusChip> : null}
                      {pain !== null ? (
                        <StatusChip tone={pain >= 4 ? 'danger' : 'neutral'} icon={pain >= 4 ? <AlertIcon /> : undefined}>
                          Pain {pain}
                        </StatusChip>
                      ) : null}
                    </span>
                  </td>
                  {showFlags ? (
                    <td className="px-2 py-2">
                      {setFlags.length ? (
                        <span className="flex flex-wrap items-center gap-1.5">
                          {setFlags.map((flag) => (
                            <span
                              key={flag.code}
                              data-testid={`session-flag-${flag.code}-set-${set.setIndex}`}
                              className="inline-flex"
                            >
                              <StatusChip tone={FLAG_TONES[flag.severity]} icon={<AlertIcon />}>
                                {flagLabel(flag.code)}
                              </StatusChip>
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-[12px] text-ink-muted">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
            {sets.length === 0 ? (
              <tr>
                <td colSpan={showFlags ? 7 : 6} className="px-2 py-3 text-[13px] text-ink-muted">
                  No sets were logged for this movement — the prescription was {prescription.sets} set
                  {prescription.sets === 1 ? '' : 's'}.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Route screens — the same view, wired to each side's entry point
   ------------------------------------------------------------------------ */

/** The lifter's session, opened from the Grid's history. No coach layer. */
export function LifterSessionScreen({ sessionId }: { sessionId: string }) {
  return (
    <SessionDetailView
      sessionId={sessionId}
      backTo="/grid"
      backLabel="Back to the Grid"
    />
  );
}

/** The coach's read of a client's session: flags and highlighting on. */
export function CoachSessionScreen({ clientId, sessionId }: { clientId: string; sessionId: string }) {
  return (
    <SessionDetailView
      sessionId={sessionId}
      clientId={clientId}
      showFlags
      backTo={`/crm/clients/${encodeURIComponent(clientId)}`}
      backLabel="Back to the client"
    />
  );
}
